// src/services/currencyService.js

/**
 * Currency Conversion Service
 *
 * Provides currency conversion functionality using real-time exchange rates
 * Falls back to static rates if API is unavailable
 */

// Fallback static exchange rates (base: USD)
// Used when API is unavailable or for initial load
const FALLBACK_EXCHANGE_RATES = {
  USD: 1.0,
  EUR: 0.92,
  GBP: 0.79,
  JPY: 149.50,
  CAD: 1.36,
  AUD: 1.53,
  CHF: 0.88,
  CNY: 7.24,
  INR: 83.12,
  MXN: 17.08,
  BRL: 4.97,
  ZAR: 18.65,
  NZD: 1.67,
  SGD: 1.35,
  HKD: 7.83,
  KRW: 1319.50,
  SEK: 10.87,
  NOK: 10.78,
  DKK: 6.87,
  PLN: 4.03,
  THB: 35.73,
  IDR: 15678.0,
  MYR: 4.73,
  PHP: 56.35,
  CZK: 23.18,
  HUF: 360.50,
  ILS: 3.73,
  CLP: 976.50,
  AED: 3.67,
  SAR: 3.75,
};

// Current exchange rates (loaded from API or fallback)
let EXCHANGE_RATES = { ...FALLBACK_EXCHANGE_RATES };

// Cache settings
const CACHE_KEY = 'travaili_exchange_rates';
const CACHE_TIMESTAMP_KEY = 'travaili_exchange_rates_timestamp';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

// Exchange rate API configuration
// Using exchangerate-api.com free tier (1,500 requests/month)
const API_BASE_URL = 'https://open.exchangerate-api.com/v6/latest/USD';

/**
 * Fetch real-time exchange rates from API
 * @returns {Promise<Object>} Exchange rates object
 */
const fetchExchangeRates = async () => {
  try {
    console.log('Fetching real-time exchange rates...');
    const response = await fetch(API_BASE_URL);

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data = await response.json();

    if (data.rates) {
      console.log('✅ Successfully fetched real-time exchange rates');
      return data.rates;
    } else {
      throw new Error('Invalid API response format');
    }
  } catch (error) {
    console.warn('Failed to fetch exchange rates from API:', error.message);
    console.log('Falling back to static rates');
    return null;
  }
};

/**
 * Load exchange rates from cache
 * @returns {Object|null} Cached rates or null if expired/not found
 */
const loadCachedRates = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    const cachedRates = localStorage.getItem(CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

    if (!cachedRates || !timestamp) {
      return null;
    }

    const age = Date.now() - parseInt(timestamp, 10);
    if (age > CACHE_DURATION) {
      console.log('Exchange rate cache expired');
      return null;
    }

    console.log('✅ Loaded exchange rates from cache');
    return JSON.parse(cachedRates);
  } catch (error) {
    console.warn('Error loading cached rates:', error);
    return null;
  }
};

/**
 * Save exchange rates to cache
 * @param {Object} rates - Exchange rates to cache
 */
const saveCachedRates = (rates) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    localStorage.setItem(CACHE_KEY, JSON.stringify(rates));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
    console.log('💾 Saved exchange rates to cache');
  } catch (error) {
    console.warn('Error saving rates to cache:', error);
  }
};

/**
 * Initialize exchange rates (load from cache or fetch from API)
 * @returns {Promise<void>}
 */
export const initializeExchangeRates = async () => {
  // Try to load from cache first
  const cachedRates = loadCachedRates();
  if (cachedRates) {
    EXCHANGE_RATES = cachedRates;
    return;
  }

  // If cache miss, fetch from API
  const freshRates = await fetchExchangeRates();
  if (freshRates) {
    EXCHANGE_RATES = freshRates;
    saveCachedRates(freshRates);
  } else {
    // If API fails, use fallback rates
    EXCHANGE_RATES = { ...FALLBACK_EXCHANGE_RATES };
    console.log('Using fallback exchange rates');
  }
};

/**
 * Manually refresh exchange rates from API
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const refreshExchangeRates = async () => {
  const freshRates = await fetchExchangeRates();
  if (freshRates) {
    EXCHANGE_RATES = freshRates;
    saveCachedRates(freshRates);
    return true;
  }
  return false;
};

/**
 * Get the current exchange rates being used
 * @returns {Object} Current exchange rates
 */
export const getCurrentRates = () => {
  return { ...EXCHANGE_RATES };
};

/**
 * Get the age of cached rates in milliseconds
 * @returns {number|null} Age in ms, or null if no cache
 */
export const getCacheAge = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (!timestamp) return null;
    return Date.now() - parseInt(timestamp, 10);
  } catch (error) {
    return null;
  }
};

// Auto-initialize on module load
if (typeof window !== 'undefined') {
  initializeExchangeRates().catch(err => {
    console.warn('Failed to initialize exchange rates:', err);
  });
}

/**
 * Convert amount from one currency to another
 * @param {number} amount - Amount to convert
 * @param {string} fromCurrency - Source currency code (e.g., 'USD')
 * @param {string} toCurrency - Target currency code (e.g., 'EUR')
 * @returns {number} Converted amount
 */
export const convertCurrency = (amount, fromCurrency, toCurrency) => {
  if (!amount || amount <= 0) return 0;
  if (!fromCurrency || !toCurrency) return amount;
  if (fromCurrency === toCurrency) return amount;

  const fromRate = EXCHANGE_RATES[fromCurrency.toUpperCase()];
  const toRate = EXCHANGE_RATES[toCurrency.toUpperCase()];

  if (!fromRate || !toRate) {
    console.warn(`Exchange rate not found for ${fromCurrency} or ${toCurrency}`);
    return amount;
  }

  // Convert to USD first, then to target currency
  const amountInUSD = amount / fromRate;
  const convertedAmount = amountInUSD * toRate;

  return Math.round(convertedAmount * 100) / 100; // Round to 2 decimal places
};

/**
 * Format currency amount with currency symbol
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (e.g., 'USD')
 * @returns {string} Formatted currency string (e.g., '$100.00')
 */
export const formatCurrency = (amount, currency = 'USD') => {
  if (!amount && amount !== 0) return '';

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
  }
};

/**
 * Get currency symbol for a currency code
 * @param {string} currency - Currency code (e.g., 'USD')
 * @returns {string} Currency symbol (e.g., '$')
 */
export const getCurrencySymbol = (currency = 'USD') => {
  const symbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    CAD: 'CA$',
    AUD: 'A$',
    CHF: 'CHF',
    CNY: '¥',
    INR: '₹',
    MXN: 'MX$',
    BRL: 'R$',
    ZAR: 'R',
    NZD: 'NZ$',
    SGD: 'S$',
    HKD: 'HK$',
    KRW: '₩',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    PLN: 'zł',
    THB: '฿',
    IDR: 'Rp',
    MYR: 'RM',
    PHP: '₱',
    CZK: 'Kč',
    HUF: 'Ft',
    ILS: '₪',
    CLP: 'CLP$',
    AED: 'د.إ',
    SAR: '﷼',
  };

  return symbols[currency.toUpperCase()] || currency.toUpperCase();
};

/**
 * Extract currency from price string
 * @param {string} priceString - Price string (e.g., '$100.00', '100 USD')
 * @returns {object} Object with currency and amount
 */
export const extractCurrencyFromString = (priceString) => {
  if (!priceString) return { currency: 'USD', amount: 0 };

  const str = priceString.toString().trim();

  // Common currency symbols
  const currencyPatterns = [
    { pattern: /^\$\s*([\d,]+\.?\d*)/, currency: 'USD' },
    { pattern: /^€\s*([\d,]+\.?\d*)/, currency: 'EUR' },
    { pattern: /^£\s*([\d,]+\.?\d*)/, currency: 'GBP' },
    { pattern: /^¥\s*([\d,]+\.?\d*)/, currency: 'JPY' },
    { pattern: /^₹\s*([\d,]+\.?\d*)/, currency: 'INR' },
    { pattern: /([\d,]+\.?\d*)\s*USD/i, currency: 'USD' },
    { pattern: /([\d,]+\.?\d*)\s*EUR/i, currency: 'EUR' },
    { pattern: /([\d,]+\.?\d*)\s*GBP/i, currency: 'GBP' },
    { pattern: /([\d,]+\.?\d*)\s*JPY/i, currency: 'JPY' },
  ];

  for (const { pattern, currency } of currencyPatterns) {
    const match = str.match(pattern);
    if (match) {
      const amount = parseFloat(match[1].replace(/,/g, ''));
      return { currency, amount };
    }
  }

  // If no currency found, try to extract just the number
  const numberMatch = str.match(/([\d,]+\.?\d*)/);
  if (numberMatch) {
    const amount = parseFloat(numberMatch[1].replace(/,/g, ''));
    return { currency: 'USD', amount }; // Default to USD
  }

  return { currency: 'USD', amount: 0 };
};

/**
 * Convert and format price for display
 * @param {number|string} price - Original price
 * @param {string} fromCurrency - Source currency
 * @param {string} toCurrency - Target currency (user's preferred currency)
 * @returns {object} Object with convertedAmount and formattedPrice
 */
export const convertAndFormatPrice = (price, fromCurrency, toCurrency) => {
  if (!price) return { convertedAmount: 0, formattedPrice: '' };

  let amount = price;
  let sourceCurrency = fromCurrency || 'USD';

  // If price is a string, extract currency and amount
  if (typeof price === 'string') {
    const extracted = extractCurrencyFromString(price);
    amount = extracted.amount;
    sourceCurrency = fromCurrency || extracted.currency;
  }

  const convertedAmount = convertCurrency(amount, sourceCurrency, toCurrency);
  const formattedPrice = formatCurrency(convertedAmount, toCurrency);

  return {
    convertedAmount,
    formattedPrice,
    originalAmount: amount,
    originalCurrency: sourceCurrency,
    targetCurrency: toCurrency,
  };
};
